import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import LoadingButton from "@mui/lab/LoadingButton";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { useAppDispatch } from "app/hooks";
import { neutral } from "app/themes/colors";
import Dialog from "components/dialog";

import { getConfigurableLineItemsHeaders } from "../merge-tags/merge-tags-values/common/configurable-merge-tags";
import { selectDataParamsById, setDataParams } from "../word-processor.slice";
import DeviceSettings from "./components/device-settings.component";
import GeneralSettings from "./components/general-settings.component";
import ServicesSettings from "./components/services-settings.component";
import TotalsSettings from "./components/totals-settings.component";
import { createDefaultValues } from "./utils";

export interface SelectSaleConfigurationValues {
  // general
  include_hearing_aids: boolean;
  include_domes: boolean;
  include_receivers: boolean;
  include_tubes: boolean;
  include_earmolds: boolean;
  include_batteries: boolean;
  include_chargers: boolean;
  include_remotes: boolean;
  include_others: boolean;
  include_services_dispensing_fees: boolean;
  include_totals: boolean;

  //devices
  hearing_aids_show_product: boolean;
  hearing_aids_show_serial: boolean;
  hearing_aids_show_manufacturer: boolean;
  hearing_aids_show_model: boolean;
  hearing_aids_show_color: boolean;
  hearing_aids_show_battery: boolean;
  hearing_aids_show_mfr_warranty_exp: boolean;
  hearing_aids_show_lnd_warranty_exp: boolean;
  hearing_aids_show_service_plan_exp: boolean;
  hearing_aids_show_lnd_deductible: boolean;
  hearing_aids_show_price: boolean;
  hearing_aids_show_quantity: boolean;
  hearing_aids_show_patient_resp: boolean;
  hearing_aids_show_insurance_resp: boolean;
  hearing_aids_show_managed_care_resp: boolean;
  hearing_aids_show_cpt_code: boolean;
  hearing_aids_show_ear: boolean;

  domes_show_product: boolean;
  domes_show_serial: boolean;
  domes_show_manufacturer: boolean;
  domes_show_model: boolean;
  domes_show_mfr_warranty_exp: boolean;
  domes_show_lnd_warranty_exp: boolean;
  domes_show_lnd_deductible: boolean;
  domes_show_price: boolean;
  domes_show_quantity: boolean;
  domes_show_patient_resp: boolean;
  domes_show_insurance_resp: boolean;
  domes_show_managed_care_resp: boolean;
  domes_show_cpt_code: boolean;
  domes_show_dome_type: boolean;
  domes_show_dome_size: boolean;
  domes_show_ear: boolean;

  receivers_show_product: boolean;
  receivers_show_manufacturer: boolean;
  receivers_show_model: boolean;
  receivers_show_color: boolean;
  receivers_show_serial: boolean;
  receivers_show_mfr_warranty_exp: boolean;
  receivers_show_lnd_warranty_exp: boolean;
  receivers_show_lnd_deductible: boolean;
  receivers_show_price: boolean;
  receivers_show_quantity: boolean;
  receivers_show_patient_resp: boolean;
  receivers_show_insurance_resp: boolean;
  receivers_show_managed_care_resp: boolean;
  receivers_show_cpt_code: boolean;
  receivers_show_gain: boolean;
  receivers_show_length: boolean;
  receivers_show_ear: boolean;

  tubes_show_product: boolean;
  tubes_show_manufacturer: boolean;
  tubes_show_model: boolean;
  tubes_show_serial: boolean;
  tubes_show_mfr_warranty_exp: boolean;
  tubes_show_lnd_warranty_exp: boolean;
  tubes_show_lnd_deductible: boolean;
  tubes_show_price: boolean;
  tubes_show_quantity: boolean;
  tubes_show_patient_resp: boolean;
  tubes_show_insurance_resp: boolean;
  tubes_show_managed_care_resp: boolean;
  tubes_show_cpt_code: boolean;
  tubes_show_ear: boolean;

  earmolds_show_product: boolean;
  earmolds_show_manufacturer: boolean;
  earmolds_show_model: boolean;
  earmolds_show_serial: boolean;
  earmolds_show_mfr_warranty_exp: boolean;
  earmolds_show_lnd_warranty_exp: boolean;
  earmolds_show_lnd_deductible: boolean;
  earmolds_show_price: boolean;
  earmolds_show_quantity: boolean;
  earmolds_show_patient_resp: boolean;
  earmolds_show_insurance_resp: boolean;
  earmolds_show_managed_care_resp: boolean;
  earmolds_show_cpt_code: boolean;
  earmolds_show_material: boolean;
  earmolds_show_canal_length: boolean;
  earmolds_show_canal_venting: boolean;
  earmolds_show_ear: boolean;

  batteries_show_product: boolean;
  batteries_show_manufacturer: boolean;
  batteries_show_model: boolean;
  batteries_show_color: boolean;
  batteries_show_serial: boolean;
  batteries_show_mfr_warranty_exp: boolean;
  batteries_show_lnd_warranty_exp: boolean;
  batteries_show_lnd_deductible: boolean;
  batteries_show_price: boolean;
  batteries_show_quantity: boolean;
  batteries_show_patient_resp: boolean;
  batteries_show_insurance_resp: boolean;
  batteries_show_managed_care_resp: boolean;
  batteries_show_cpt_code: boolean;

  //chargers
  chargers_show_product: boolean;
  chargers_show_manufacturer: boolean;
  chargers_show_model: boolean;
  chargers_show_serial: boolean;
  chargers_show_mfr_warranty_exp: boolean;
  chargers_show_lnd_warranty_exp: boolean;
  chargers_show_lnd_deductible: boolean;
  chargers_show_price: boolean;
  chargers_show_quantity: boolean;
  chargers_show_patient_resp: boolean;
  chargers_show_insurance_resp: boolean;
  chargers_show_managed_care_resp: boolean;
  chargers_show_cpt_code: boolean;

  remotes_show_product: boolean;
  remotes_show_manufacturer: boolean;
  remotes_show_model: boolean;
  remotes_show_serial: boolean;
  remotes_show_mfr_warranty_exp: boolean;
  remotes_show_lnd_warranty_exp: boolean;
  remotes_show_lnd_deductible: boolean;
  remotes_show_price: boolean;
  remotes_show_quantity: boolean;
  remotes_show_patient_resp: boolean;
  remotes_show_insurance_resp: boolean;
  remotes_show_managed_care_resp: boolean;
  remotes_show_cpt_code: boolean;

  others_show_product: boolean;
  others_show_manufacturer: boolean;
  others_show_model: boolean;
  others_show_serial: boolean;
  others_show_mfr_warranty_exp: boolean;
  others_show_lnd_warranty_exp: boolean;
  others_show_lnd_deductible: boolean;
  others_show_price: boolean;
  others_show_quantity: boolean;
  others_show_patient_resp: boolean;
  others_show_insurance_resp: boolean;
  others_show_managed_care_resp: boolean;
  others_show_cpt_code: boolean;

  //services

  services_show_cpt_code: boolean;
  services_show_product: boolean;
  services_show_description: boolean;
  services_show_price: boolean;
  services_show_quantity: boolean;

  //totals
  total_show_services_dispensing_fees: boolean;
  total_show_discounts: boolean;
  total_tax: boolean;
  total_amount: boolean;
  total_patient_payments: boolean;
  total_insurance_payments: boolean;
  amount_due_from_insurance: boolean;
  amount_due_from_patient: boolean;
  balance_due: boolean;
  total_show_cost_of_ha: boolean;
}

interface WordProcessorSelectSaleConfigurationDialogProps {
  readonly id: string;
}

export default function WordProcessorSelectSaleConfigurationDialog({
  id
}: WordProcessorSelectSaleConfigurationDialogProps) {
  const { zIndex } = useTheme();
  const dispatch = useAppDispatch();
  const dataParams =
    useSelector((state: any) => selectDataParamsById(state, id)) || {};

  const defaultValues: SelectSaleConfigurationValues = useMemo(() => {
    const data = (dataParams?.saleConfiguration ||
      {}) as SelectSaleConfigurationValues;
    return createDefaultValues(data);
  }, [dataParams?.saleConfiguration]);

  const formMethods = useForm<SelectSaleConfigurationValues>({
    mode: "onBlur",
    defaultValues
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isValid }
  } = formMethods;

  const allValues = watch();

  const tableColumns = useMemo(() => {
    return getConfigurableLineItemsHeaders(allValues);
  }, [allValues]);

  // Calculate selected counts for each tab
  const generalCount = useMemo(() => {
    const generalFields = [
      "include_hearing_aids",
      "include_domes",
      "include_receivers",
      "include_tubes",
      "include_earmolds",
      "include_batteries",
      "include_chargers",
      "include_remotes",
      "include_others",
      "include_services_dispensing_fees",
      "include_totals"
    ];
    return generalFields.filter(
      (field) => allValues?.[field as keyof typeof allValues]
    ).length;
  }, [allValues]);

  const devicesCount = useMemo(() => {
    const devicePrefixes = [
      "hearing_aids",
      "domes",
      "receivers",
      "tubes",
      "earmolds",
      "batteries",
      "chargers",
      "remotes",
      "others"
    ];

    if (!allValues) return 0;

    return Object.entries(allValues).filter(([key]) => {
      return (
        devicePrefixes.some((prefix) => key.startsWith(`${prefix}_show_`)) &&
        Boolean(allValues[key as keyof SelectSaleConfigurationValues])
      );
    }).length;
  }, [allValues]);

  const servicesCount = useMemo(() => {
    const servicesFields = [
      "services_show_cpt_code",
      "services_show_product",
      "services_show_description",
      "services_show_price",
      "services_show_quantity"
    ];
    return servicesFields.filter(
      (field) => allValues?.[field as keyof typeof allValues]
    ).length;
  }, [allValues]);

  const totalsCount = useMemo(() => {
    const totalsFields = [
      "total_show_services_dispensing_fees",
      "total_show_discounts",
      "total_tax",
      "total_amount",
      "total_patient_payments",
      "total_insurance_payments",
      "amount_due_from_insurance",
      "amount_due_from_patient",
      "balance_due",
      "total_show_cost_of_ha"
    ];
    return totalsFields.filter(
      (field) => allValues?.[field as keyof typeof allValues]
    ).length;
  }, [allValues]);

  const handleClose = useCallback(() => {
    reset();

    dispatch(setDataParams({ id, openSelectSaleConfigurationDialog: false }));
  }, [dispatch, id, reset]);

  const saleId = dataParams?.saleId;

  const handleSave = useCallback(
    async (data: SelectSaleConfigurationValues) => {
      let result: any = {};

      const {
        include_hearing_aids,
        include_domes,
        include_receivers,
        include_tubes,
        include_earmolds,
        include_chargers,
        include_batteries,
        include_remotes,
        include_others,
        include_services_dispensing_fees
      } = data;

      result = Object.fromEntries(
        Object.entries(data).filter(([key, value]) => {
          if (!include_hearing_aids && key.startsWith("hearing_aids_")) {
            return false;
          } else if (!include_domes && key.startsWith("domes_")) {
            return false;
          } else if (!include_receivers && key.startsWith("receivers_")) {
            return false;
          } else if (!include_tubes && key.startsWith("tubes_")) {
            return false;
          } else if (!include_earmolds && key.startsWith("earmolds_")) {
            return false;
          } else if (!include_batteries && key.startsWith("batteries_")) {
            return false;
          } else if (!include_chargers && key.startsWith("chargers_")) {
            return false;
          } else if (!include_remotes && key.startsWith("remotes_")) {
            return false;
          } else if (!include_others && key.startsWith("others_")) {
            return false;
          } else if (
            !include_services_dispensing_fees &&
            key.startsWith("services_")
          ) {
            return false;
          }
          return Boolean(value);
        })
      );

      dispatch(
        setDataParams({
          id,
          saleId: Number(saleId),
          saleConfiguration: result
        })
      );
      handleClose();
    },
    [dispatch, handleClose, id, saleId]
  );

  const [value, setValue] = useState("general");

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  // Accordion expanded states
  const [generalExpanded, setGeneralExpanded] = useState(true);
  const [servicesExpanded, setServicesExpanded] = useState(true);
  const [totalsExpanded, setTotalsExpanded] = useState(true);
  const [deviceExpandedStates, setDeviceExpandedStates] = useState<
    Record<string, boolean>
  >({
    hearing_aids: true,
    domes: true,
    receivers: true,
    tubes: true,
    earmolds: true,
    batteries: true,
    chargers: true,
    remotes: true,
    others: true
  });

  const handleGeneralAccordionChange = useCallback(
    (event: React.SyntheticEvent, expanded: boolean) => {
      setGeneralExpanded(expanded);
    },
    []
  );

  const handleServicesAccordionChange = useCallback(
    (event: React.SyntheticEvent, expanded: boolean) => {
      setServicesExpanded(expanded);
    },
    []
  );

  const handleTotalsAccordionChange = useCallback(
    (event: React.SyntheticEvent, expanded: boolean) => {
      setTotalsExpanded(expanded);
    },
    []
  );

  const handleExpandedStateChange = useCallback(
    (key: string, expanded: boolean) => {
      if (key === "services") {
        setServicesExpanded(expanded);
      } else if (key === "totals") {
        setTotalsExpanded(expanded);
      } else {
        setDeviceExpandedStates((prev) => ({
          ...prev,
          [key]: expanded
        }));
      }
    },
    []
  );

  const handleDeviceExpandedChange = useCallback(
    (namePrefix: string) =>
      (event: React.SyntheticEvent, expanded: boolean) => {
        setDeviceExpandedStates((prev) => ({
          ...prev,
          [namePrefix]: expanded
        }));
      },
    []
  );

  return (
    <Dialog
      title="Please Define Invoice Merge Tag Parameters"
      onClose={handleClose}
      open={Boolean(id && dataParams.openSelectSaleConfigurationDialog)}
      minWidth={770}
      dialogContentProps={{
        sx: {
          display: "flex",
          alignItems: "center",
          pb: "20px",
          flexDirection: "column",
          background: neutral[50]
        }
      }}
      sx={{ zIndex: zIndex.snackbar + 3 }}
    >
      <Stack height={710} maxHeight={710} width="100%" mb={1} gap={1}>
        <TabContext value={value}>
          <TabList
            onChange={handleChange}
            sx={{
              "& .MuiTabs-flexContainer": {
                overflowX: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none"
                },
                WebkitOverflowScrolling: { xs: "touch", md: "auto" }
              }
            }}
          >
            <Tab
              label={
                <Badge badgeContent={generalCount} color="primary">
                  General Settings
                </Badge>
              }
              value="general"
            />
            <Tab
              label={
                <Badge badgeContent={devicesCount} color="primary">
                  Device Settings
                </Badge>
              }
              value="devices"
            />
            <Tab
              label={
                <Badge badgeContent={servicesCount} color="primary">
                  Services Settings
                </Badge>
              }
              value="services"
            />
            <Tab
              label={
                <Badge badgeContent={totalsCount} color="primary">
                  Totals Settings
                </Badge>
              }
              value="totals"
            />
          </TabList>

          <FormProvider {...formMethods}>
            <Stack
              id="settings-form"
              data-testid="settings-form"
              sx={{
                height: "100%",
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: "8px"
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "4px"
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "4px",
                  "&:hover": {
                    background: "#a8a8a8"
                  }
                }
              }}
            >
              <TabPanel value="general">
                <GeneralSettings
                  expanded={generalExpanded}
                  onChange={handleGeneralAccordionChange}
                  onExpandedStateChange={handleExpandedStateChange}
                />
              </TabPanel>
              <TabPanel value="devices">
                <DeviceSettings
                  expandedStates={deviceExpandedStates}
                  onExpandedChange={handleDeviceExpandedChange}
                />
              </TabPanel>
              <TabPanel value="services">
                <ServicesSettings
                  expanded={servicesExpanded}
                  onChange={handleServicesAccordionChange}
                />
              </TabPanel>
              <TabPanel value="totals">
                <TotalsSettings
                  expanded={totalsExpanded}
                  onChange={handleTotalsAccordionChange}
                />
              </TabPanel>
            </Stack>
          </FormProvider>
        </TabContext>
        <Stack mb={1.5}>
          <Typography variant="body3" mt={1} mb={1} sx={{ fontSize: 20 }}>
            Table columns
          </Typography>
          <Stack direction="row" columnGap={1} rowGap={0.5} flexWrap="wrap">
            {(tableColumns.length ? tableColumns : ["No columns"]).map(
              (column) => (
                <Typography
                  key={column}
                  variant="body1"
                  sx={{
                    background: neutral[100],
                    padding: "4px",
                    borderRadius: "4px",
                    color: neutral[800]
                  }}
                >
                  {column}
                </Typography>
              )
            )}
          </Stack>
          <Typography variant="body3" mt={2} mb={0} sx={{ fontSize: 20 }}>
            {allValues.include_totals ? "Totals Included" : "No totals"}
          </Typography>
        </Stack>
      </Stack>
      <DialogActions>
        <Button
          variant="outlined"
          size="small"
          onClick={handleClose}
          style={{ background: "white" }}
        >
          Cancel
        </Button>

        <LoadingButton
          disabled={!isValid}
          variant="contained"
          size="small"
          onClick={handleSubmit(handleSave)}
        >
          Apply Parameters
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
